/**
 * Returns the quantity input step for a product based on its partial-qty setting.
 *
 * @param {{ allow_partial_quantity?: boolean }|null|undefined} product
 * @returns {number}
 */
export function productQuantityStep (product) {
    return product?.allow_partial_quantity ? 0.5 : 1;
}

/**
 * Returns the quantity input step for an order line item.
 *
 * @param {{ partial_qty?: boolean, product?: { allow_partial_quantity?: boolean } }|null|undefined} item
 * @returns {number}
 */
export function orderItemQuantityStep (item) {
    const allowPartial = item?.partial_qty ?? item?.product?.allow_partial_quantity;

    return allowPartial ? 0.5 : 1;
}

/**
 * Normalizes a quantity to the nearest valid increment for the product.
 * Whole-number products round partial quantities up to the next integer.
 *
 * @param {number|string} quantity
 * @param {{ allow_partial_quantity?: boolean }|null|undefined} product
 * @returns {number}
 */
export function normalizeQuantityForProduct (quantity, product) {
    const parsedQuantity = Number(quantity);

    if (!product?.allow_partial_quantity) {
        return Math.ceil(parsedQuantity);
    }

    return Math.round(parsedQuantity * 2) / 2;
}

/**
 * Whether split-case pricing applies for the given quantity, customer, and product.
 *
 * @param {number|string} quantity
 * @param {{ type?: string }|null|undefined} customer
 * @param {{ allow_partial_quantity?: boolean }|null|undefined} product
 * @returns {boolean}
 */
export function shouldApplySplitCaseSurcharge (quantity, customer, product) {
    if (!product?.allow_partial_quantity) {
        return false;
    }

    const parsedQuantity = Number(quantity);

    return customer?.type !== 'School' && parsedQuantity > 0 && parsedQuantity < 1;
}

/**
 * Whether split-case pricing applies for an order line item.
 *
 * @param {number|string} quantity
 * @param {{ type?: string }|null|undefined} customer
 * @param {{ partial_qty?: boolean, product?: { allow_partial_quantity?: boolean } }|null|undefined} item
 * @returns {boolean}
 */
export function shouldApplySplitCaseSurchargeForItem (quantity, customer, item) {
    const product = item?.product ?? item;
    const allowPartial = item?.partial_qty ?? product?.allow_partial_quantity;

    if (!allowPartial) {
        return false;
    }

    const parsedQuantity = Number(quantity);

    return customer?.type !== 'School' && parsedQuantity > 0 && parsedQuantity < 1;
}
